import React, { useEffect, useRef, useState } from 'react'
import { Select, Spin, Tag } from 'antd'
import http from '../../util/Api'

/**
 * One search box for an asset's classification - model, sub-type and type are
 * found together and chosen together, as in the PV form (PreVerificationCreateSubList).
 *
 * API: GET common/asset-parameter-search/:keyword - the same endpoint the PV form
 * uses. It matches the keyword against model, sub-type AND type names and returns
 * complete { model, subType, type } rows, so a pick can never leave the three
 * inconsistent.
 *
 * Calls: only from 3 characters, debounced 400 ms, and a reply that arrives after
 * a newer search started is dropped. Not cached - the masters can change (models
 * are created inline from this same form). The keyword is regex-escaped because
 * the backend uses it as a regular expression; a "(" or "+" in a model name
 * would otherwise error out instead of matching.
 *
 * Exact ids only: the pick sets the ids of the chosen row. Nothing is matched by
 * name and nothing is pre-selected for the user.
 */

const MIN_CHARS = 3
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const idOf = (v) => (v && typeof v === 'object' ? v._id : v) || null

export const labelOf = (row) => [row.model && row.model.name, row.subType && row.subType.name, row.type && row.type.name]
  .filter(Boolean).join(' · ')

const ClassificationSearch = ({ value, mixed, selected, onPick, onClear, disabled }) => {
  const [rows, setRows] = useState([])
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [failed, setFailed] = useState(false)
  const timer = useRef(null)
  const seq = useRef(0)

  useEffect(() => () => clearTimeout(timer.current), [])

  const search = (text) => {
    const q = (text || '').trim()
    setQuery(q)
    clearTimeout(timer.current)
    if (q.length < MIN_CHARS) { seq.current += 1; setRows([]); setSearching(false); setFailed(false); return }
    timer.current = setTimeout(() => {
      const mine = ++seq.current
      setSearching(true)
      setFailed(false)
      http.get(`common/asset-parameter-search/${encodeURIComponent(escapeRegex(q))}`)
        .then(res => {
          if (mine !== seq.current) return
          const seen = new Set()
          const list = (Array.isArray(res.data) ? res.data : [])
            .filter(d => d && d.model && d.model._id && d.subType && d.subType._id && d.type && d.type._id)
            .filter(d => {
              const k = `${d.model._id}|${d.subType._id}|${d.type._id}`
              if (seen.has(k)) return false
              seen.add(k)
              return true
            })
          setRows(list)
        })
        .catch(() => { if (mine === seq.current) { setRows([]); setFailed(true) } })
        .finally(() => { if (mine === seq.current) setSearching(false) })
    }, 400)
  }

  // Keep the current pick selectable/visible even when it is not in the latest results.
  const options = rows.slice()
  if (value && selected && !options.some(r => String(r.model._id) === String(value))) options.unshift(selected)
  // A model id with no known names is never rendered as a raw id.
  const shown = value && options.some(r => String(r.model._id) === String(value))

  const notFound = searching ? <Spin size="small" />
    : failed ? 'Search failed — try again'
      : query.length < MIN_CHARS ? `Type ${MIN_CHARS}+ letters of a model, sub-type or type`
        : 'No model, sub-type or type matches'

  return (
    <Select
      showSearch allowClear size="small" style={{ flex: 1, minWidth: 0 }}
      disabled={disabled}
      value={shown ? String(value) : undefined}
      placeholder={mixed ? 'Mixed — search to set all'
        : value ? 'Model set, not in the loaded lists — search to change'
          : `Search model, sub-type or type (${MIN_CHARS}+ letters)`}
      filterOption={false}
      defaultActiveFirstOption={false}
      onSearch={search}
      notFoundContent={notFound}
      optionLabelProp="label"
      dropdownClassName="rcx-cls-dd"
      onChange={(v) => {
        if (v === undefined) return onClear()
        const row = options.find(r => String(r.model._id) === String(v))
        if (row) onPick(row)
      }}
    >
      {options.map(r => (
        <Select.Option key={`${r.model._id}|${r.subType._id}|${r.type._id}`} value={String(r.model._id)} label={labelOf(r)}>
          <Tag color="blue">{r.model.name}</Tag>
          <Tag color="geekblue">{r.subType.name}</Tag>
          <Tag color="purple">{r.type.name}</Tag>
        </Select.Option>
      ))}
    </Select>
  )
}

// The full row for one model id, read from the same search endpoint by the
// model's name and matched on the id exactly. Used once, right after a model is
// created inline, when its sub-type / type names are not known locally. Resolves
// null when no row for that id comes back.
export const findRowForModel = (modelId, modelName) => {
  if (!modelId || !modelName) return Promise.resolve(null)
  return http.get(`common/asset-parameter-search/${encodeURIComponent(escapeRegex(String(modelName)))}`)
    .then(res => (Array.isArray(res.data) ? res.data : [])
      .find(d => d && d.model && String(d.model._id) === String(modelId) && d.subType && d.subType._id && d.type && d.type._id) || null)
}

// The { model, subType, type } row for ids already set on a record, built from
// the master lists the form has loaded. Null when any part is unknown, so a
// partial classification is never shown as a complete one.
export const rowFromMasters = (masters, modelId) => {
  if (!masters || !modelId) return null
  const model = (masters.assetModel || []).find(m => String(m._id) === String(modelId))
  if (!model) return null
  const subId = idOf(model.assetSubType)
  const subType = (masters.equipmentSubType || []).find(s => String(s._id) === String(subId))
  if (!subType) return null
  const typeId = idOf(subType.assetType)
  const type = (masters.assetType || []).find(t => String(t._id) === String(typeId))
  if (!type) return null
  return {
    model: { _id: model._id, name: model.name, isCountable: model.isCountable },
    subType: { _id: subType._id, name: subType.name },
    type: { _id: type._id, name: type.name }
  }
}

export default ClassificationSearch
